import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Text,
  Button,
  Switch,
  useColorModeValue,
  useToast,
  HStack,
  Link,
  Flex,
  Spacer,
  Checkbox,
  Select,
  Input,
  Textarea,
} from "@chakra-ui/react";
import {
  CopyIcon,
  EmailIcon,
  CalendarIcon,
  ExternalLinkIcon,
} from "@chakra-ui/icons";
import { CopyToClipboard } from "react-copy-to-clipboard";
import { cloneDeep, isEqual } from "lodash";
import { HOST, getBid, cqAPI } from "../../lib/Auth";
import { useAppContext } from "../../AppContext";

// Helpers
const dayOfWeek = ["Sunday","Monday","Tuesday","Wednesday","Thursday","Friday","Saturday"];
const hours = [...Array(24).keys()]; // [0..23]
const formatHour = (h) => `${String(h).padStart(2, "0")}:00`;

// Patterns
const NAME_PATTERN = /[^\ ]{3,}/; 
const WEBSITE_PATTERN = /^(|https?\:\/\/[^\.]{1,}(\.[^\.]{1,})*\.[^\.]{2,})$/;

const DisplayInfo = () => {
  const toast = useToast();
  const bgColor = useColorModeValue("gray.100", "gray.700");
  const { isAppInTestDebugMode, setIsAppInTestDebugMode } = useAppContext();

  // Single "formData" and "originalData" to track changes
  const [formData, setFormData] = useState({
    name: "",
    website: "",
    businessHours: {},
    cancellationPolicy: {
      enforce: false,
      days: 0,
      fee: 0,
      reschedule: false,
    },
    outgoingAnnouncement: {
      enabled: false,
      text: "",
    },
  });
  const [originalData, setOriginalData] = useState(cloneDeep(formData));

  // For controlling the Save button
  const [isDirty, setIsDirty] = useState(false);

  // LOAD data from server on mount
  const fetchAllSettings = useCallback(async () => {
    try {
      // 1) Fetch Name
      const [
        nameResponse,
        websiteResponse,
        bhResponse,
        enforceResp,
        daysResp,
        feeResp,
        reschedulingResp,
        outgoingAnnouncement,
      ] = await Promise.all([
        cqAPI.get("/settings/business?id=name"),
        cqAPI.get("/settings/business?id=website"),
        cqAPI.get("/settings/business?id=business_hours"),
        cqAPI.get("/settings/business?id=cancellation_policy.enforce"),
        cqAPI.get("/settings/business?id=cancellation_policy.days"),
        cqAPI.get("/settings/business?id=cancellation_policy.fee"),
        cqAPI.get("/settings/business?id=cancellation_policy.reschedule"),
        cqAPI.get("/settings/business?id=outgoing_message"),
      ]);

      const nameValue = nameResponse.data || "";
      const websiteValue = websiteResponse.data || "";
      const businessHoursValue = bhResponse.data || {};

      const cancellationPolicyValue = {
        enforce: !!enforceResp.data, // force boolean
        days: Number(daysResp.data) || 0,
        fee: Number(feeResp.data) || 0,
        reschedule: !!reschedulingResp.data,
      };

      const freshData = {
        name: nameValue,
        website: websiteValue,
        businessHours: businessHoursValue,
        cancellationPolicy: cancellationPolicyValue,
        outgoingAnnouncement : outgoingAnnouncement.data
      };

      setFormData(freshData);
      setOriginalData(cloneDeep(freshData));
      setIsDirty(false);
    } catch (error) {
      console.error(error);
      toast({
        title: "Error fetching data",
        description: error.message,
        status: "error",
      });
    }
  }, [toast]);

  useEffect(() => {
    fetchAllSettings();
  }, [fetchAllSettings]);

  // Whenever formData changes, check if it differs from originalData
  useEffect(() => {
    setIsDirty(!isEqual(formData, originalData));
  }, [formData, originalData]);

  // ----------------------------
  // Handlers for name/website
  // ----------------------------
  const handleNameChange = (e) => {
    setFormData((prev) => ({ ...prev, name: e.target.value }));
  };

  const handleWebsiteChange = (e) => {
    setFormData((prev) => ({ ...prev, website: e.target.value }));
  };

  // ----------------------------
  // Handlers for business hours
  // ----------------------------
  const handleDayEnabled = (idx, checked) => {
    const bh = { ...formData.businessHours };
    const dayKey = String(idx);
    const oldDayData = bh[dayKey] || {};
    bh[dayKey] = { ...oldDayData, enabled: checked };
    setFormData((prev) => ({ ...prev, businessHours: bh }));
  };

  const handleFromChange = (idx, newFromValue) => {
    const fromVal = parseInt(newFromValue, 10);
    const bh = { ...formData.businessHours };
    const dayKey = String(idx);
    const oldDayData = bh[dayKey] || {};
    const currentEnd = typeof oldDayData.end === "number" ? oldDayData.end : 0;

    if (fromVal > currentEnd) {
      oldDayData.end = fromVal;
    }
    oldDayData.start = fromVal;

    bh[dayKey] = oldDayData;
    setFormData((prev) => ({ ...prev, businessHours: bh }));
  };

  const handleToChange = (idx, newToValue) => {
    const toVal = parseInt(newToValue, 10);
    const bh = { ...formData.businessHours };
    const dayKey = String(idx);
    const oldDayData = bh[dayKey] || {};
    const currentStart = typeof oldDayData.start === "number" ? oldDayData.start : 0;

    if (toVal < currentStart) {
      oldDayData.start = toVal;
    }
    oldDayData.end = toVal;

    bh[dayKey] = oldDayData;
    setFormData((prev) => ({ ...prev, businessHours: bh }));
  };

  // ----------------------------
  // Handlers for cancellation policy
  // ----------------------------
  const handleEnforceChange = (e) => {
    const checked = e.target.checked;
    setFormData((prev) => ({
      ...prev,
      cancellationPolicy: {
        ...prev.cancellationPolicy,
        enforce: checked,
      },
    }));
  };

  // Convert selection (string) to number; 0 means "Unselected"
  const handleDaysChange = (e) => {
    const value = Number(e.target.value);
    setFormData((prev) => ({
      ...prev,
      cancellationPolicy: {
        ...prev.cancellationPolicy,
        days: value,
      },
    }));
  };

  // Only digits for Fee
  const handleFeeChange = (e) => {
    // Strip out any non-digit characters
    let value = e.target.value.replace(/\D/g, "");
    setFormData((prev) => ({
      ...prev,
      cancellationPolicy: {
        ...prev.cancellationPolicy,
        fee: Number(value),
      },
    }));
  };

  // Applied to Rescheduling handler — FIXED
  const handleReschedulingChange = (e) => {
    const checked = e.target.checked;
    setFormData((prev) => ({
      ...prev,
      cancellationPolicy: {
        ...prev.cancellationPolicy,
        reschedule: checked,
      },
    }));
  }; // <-- end with just };


  // ----------------------------
  // Handlers for outgoing announcement
  // ----------------------------
  const handleOutgoingEnabled = (e) => {
    const checked = e.target.checked;
    setFormData((prev) => ({
      ...prev,
      outgoingAnnouncement: {
        ...prev.outgoingAnnouncement,
        enabled: checked,
      },
    }));
  };

  const handleOutgoingText = (e) => {
    setFormData((prev) => ({
      ...prev,
      outgoingAnnouncement: {
        ...prev.outgoingAnnouncement,
        text: e.target.value,
      },
    }));
  };

  // ----------------------------
  // Final Save Handler
  // ----------------------------
  const handleSave = async () => {
    // 1) Basic validation
    if (!NAME_PATTERN.test(formData.name || "")) {
      toast({
        title: "Invalid Name",
        description: "Business Name must be at least 3 non-blank chars.",
        status: "error",
      });
      return;
    }

    if (!WEBSITE_PATTERN.test(formData.website || "")) {
      toast({
        title: "Invalid Website",
        description: "Website must be blank or a valid https://… domain.",
        status: "error",
      });
      return;
    }

    // Outgoing announcement validation
    const annText = (formData.outgoingAnnouncement?.text ?? "").trim();
    if (formData.outgoingAnnouncement?.enabled && !annText) {
      toast({
        title: "Announcement required",
        description: "Please enter the announcement text or disable the switch to continue.",
        status: "error",
      });
      return;
    }

    // 2) Build the set of changes
    const changes = [];

    // Name
    if (formData.name !== originalData.name) {
      changes.push({ key: "name", value: formData.name });
    }

    // Website
    if (formData.website !== originalData.website) {
      changes.push({ key: "website", value: formData.website });
    }

    // Business Hours
    if (!isEqual(formData.businessHours, originalData.businessHours)) {
      changes.push({
        key: "business_hours",
        value: formData.businessHours,
      });
    }

    // Cancellation Policy
    if (formData.cancellationPolicy.enforce !== originalData.cancellationPolicy.enforce) {
      changes.push({
        key: "cancellation_policy.enforce",
        value: formData.cancellationPolicy.enforce,
      });
    }
    if (formData.cancellationPolicy.days !== originalData.cancellationPolicy.days) {
      changes.push({
        key: "cancellation_policy.days",
        value: formData.cancellationPolicy.days,
      });
    }
    if (formData.cancellationPolicy.fee !== originalData.cancellationPolicy.fee) {
      changes.push({
        key: "cancellation_policy.fee",
        value: formData.cancellationPolicy.fee,
      });
    }
    if (formData.cancellationPolicy.reschedule !== originalData.cancellationPolicy.reschedule) {
      changes.push({
        key: "cancellation_policy.reschedule",
        value: formData.cancellationPolicy.reschedule,
      });
    }

    // Outgoing announcement — single object update
    const outgoingChanged =
      formData.outgoingAnnouncement.enabled !== originalData.outgoingAnnouncement.enabled ||
      formData.outgoingAnnouncement.text !== originalData.outgoingAnnouncement.text;

    if (outgoingChanged) {
      changes.push({
        key: "outgoing_message",
        value: {
          enabled: formData.outgoingAnnouncement.enabled,
          text: formData.outgoingAnnouncement.text,
        },
      });
    }

    if (changes.length === 0) {
      toast({
        title: "No changes",
        description: "You haven't changed anything.",
        status: "info",
      });
      return;
    }

    try {
      // 3) Send them in a single batch update
      const response = await cqAPI.put("/settings/business?batch=true", changes);

      if (response?.data?.msg) {
        throw new Error(response.data.msg);
      }

      // 4) On success, sync originalData with the new formData
      setOriginalData(cloneDeep(formData));
      setIsDirty(false);

      toast({
        title: "Saved",
        description: "Changes have been saved successfully.",
        status: "success",
      });
    } catch (error) {
      console.error(error);
      toast({
        title: "Error Saving",
        description: error.message,
        status: "error",
      });
    }
  };


  // -- The rest of your environment detection, snippet, etc.
  const H = HOST.split("://")[1];
  const ENV = H.startsWith("qa-") ? "qa" : H.startsWith("api") ? "prod" : "dev";

  const hostname = window.location.hostname.toLowerCase();
  const mailDomain = hostname.includes("a-dec")
    ? {
        qa: "qa-inbox-link.a-dec.com",
        prod: "inbox-link.a-dec.com",
        dev: "dev-inbox.getviva.ai",
      }[ENV]
    : {
        qa: "qa-inbox.getviva.ai",
        prod: "inbox.getviva.ai",
        dev: "dev-inbox.getviva.ai",
      }[ENV];

  const calendarLink =
    `${location.protocol}//` +
    (hostname.includes("a-dec")
      ? {
          qa: "qa-booking-link.a-dec.com",
          prod: "booking-link.a-dec.com",
          dev: "dev-booking.getviva.ai",
        }[ENV]
      : {
          qa: "qa-booking.getviva.ai",
          prod: "booking.getviva.ai",
          dev: "dev-booking.getviva.ai",
        }[ENV]) +
    `?bid=${getBid()}`;

  const APIHOST = hostname.includes("a-dec")
    ? {
        qa: "https://qa-api-link.a-dec.com",
        prod: "https://api-link.a-dec.com",
        dev: HOST,
      }[ENV]
    : HOST;

  const emailAddress = `${getBid()}@${mailDomain}`;
  const scriptSnippet = `<script src="${APIHOST}/widget.js?bid=${getBid()}" async></script>`;

  const handleCopy = (text) => {
    toast({
      title: "Copied to clipboard",
      description: `${text} has been copied to your clipboard.`,
      status: "success",
      duration: 2000,
      isClosable: true,
    });
  };

  return (
    <Box p={4} overflowY="auto" maxH="100vh" w="100%" align="left">
      {/* Top Bar: Save button */}
      <Flex textAlign="right" mb={4} position="sticky" top="0" zIndex={100} bgColor={bgColor} p={4} borderRadius="md">
        <Button
          colorScheme="blue"
          isDisabled={!isDirty}
          onClick={handleSave}
        >
          Save
        </Button>
      </Flex>

      {/* Office Information */}
      <Box mb={4} p={4} bgColor={bgColor} borderRadius="md">
        <Text fontSize="lg" mb={2} fontWeight="semibold">
          Office Information
        </Text>
        <Text fontSize="sm" mb={4} color="gray.500">
          Basic details shown on your booking pages and in patient communications.
        </Text>

        <Box mb={4}>
          <Text fontWeight="medium" mb={1}>Business Name</Text>
          <Input
            placeholder="Business Name"
            value={formData.name}
            onChange={handleNameChange}
            width="300px"
          />
        </Box>

        <Box mb={4}>
          <Text fontWeight="medium" mb={1}>Website</Text>
          <Input
            placeholder="https://mydomain.com"
            value={formData.website}
            onChange={handleWebsiteChange}
            width="300px"
          />
        </Box>
      </Box>

      {/* Business Hours */}
      <Box mb={4} p={4} bgColor={bgColor} borderRadius="md">
        <Text fontSize="lg" mb={2} fontWeight="semibold">
          Business Hours
        </Text>
        <Text fontSize="sm" mb={4} color="gray.500">
          Set your office hours. For each enabled day, choose a start and end time. The start cannot exceed the end.
        </Text>

        {/* Map each day */}
        {dayOfWeek.map((dayName, idx) => {
          const dayKey = String(idx);
          const dayData = formData.businessHours[dayKey] || {};
          const isEnabled = !!dayData.enabled;
          const startVal = typeof dayData.start === "number" ? dayData.start : 0;
          const endVal   = typeof dayData.end   === "number" ? dayData.end   : 0;

          return (
            <Box key={dayName} mb={4}>
              <Flex alignItems="center" mb={2}>
                <Checkbox
                  isChecked={isEnabled}
                  onChange={(e) => handleDayEnabled(idx, e.target.checked)}
                  mr={4}
                >
                  {dayName}
                </Checkbox>
                {isEnabled ? (
                  <Text color="blue.600" fontWeight="semibold">
                    {formatHour(startVal)} – {formatHour(endVal)}
                  </Text>
                ) : (
                  <Text fontWeight="semibold" color="gray.400">
                    Closed
                  </Text>
                )}
              </Flex>

              {isEnabled && (
                <Flex pl={6} align="center" gap={2} wrap="wrap">
                  <Text>From</Text>
                  <Select
                    width="90px"
                    value={startVal}
                    onChange={(e) => handleFromChange(idx, e.target.value)}
                  >
                    {hours.map((h) => (
                      <option key={h} value={h}>
                        {formatHour(h)}
                      </option>
                    ))}
                  </Select>

                  <Text>to</Text>

                  <Select
                    width="90px"
                    value={endVal}
                    onChange={(e) => handleToChange(idx, e.target.value)}
                  >
                    {hours.map((h) => (
                      <option key={h} value={h}>
                        {formatHour(h)}
                      </option>
                    ))}
                  </Select>
                </Flex>
              )}
            </Box>
          );
        })}
      </Box>

      {/* Late Cancellation Fee */}
      <Box mb={4} p={4} bgColor={bgColor} borderRadius="md">
        <Text fontSize="lg" mb={2} fontWeight="semibold">
          Late Cancellation Fee
        </Text>
        <Text fontSize="sm" mb={4} color="gray.500">
          Configure your late-cancellation policy. If enabled, fees and day thresholds will be applied to last-minute cancellations, and (optionally) reschedules.
        </Text>

        {/* Enable */}
        <Box mb={4}>
          <HStack alignItems="center" mb={2}>
            <Switch
              isChecked={formData.cancellationPolicy.enforce}
              onChange={handleEnforceChange}
            />
            <Text ml={2} fontWeight="medium">
              Enable
            </Text>
          </HStack>
        </Box>

        {/* Days (select) */}
        <Box mb={4}>
          <Flex alignItems="center" justifyContent="space-between">
            <Text fontWeight="medium" mr={2}>Days</Text>
            <Select
              width="120px"
              value={formData.cancellationPolicy.days}
              onChange={handleDaysChange}
              isDisabled={!formData.cancellationPolicy.enforce}
            >
              <option value={0}>Unselected</option>
              {[1,2,3,4,5,6,7].map((d) => (
                <option key={d} value={d}>{d}</option>
              ))}
            </Select>
          </Flex>
        </Box>

        {/* Fee (digits only) */}
        <Box mb={4}>
          <Flex alignItems="center" justifyContent="space-between">
            <Text fontWeight="medium" mr={2}>Fee</Text>
            <Input
              width="120px"
              placeholder="Fee"
              value={String(formData.cancellationPolicy.fee || "")}
              onChange={handleFeeChange}
              isDisabled={!formData.cancellationPolicy.enforce}
            />
          </Flex>
        </Box>

        {/* Applied to Rescheduling */}
        <Box mb={4}>
          <HStack alignItems="center" mb={2}>
            <Switch
              isChecked={formData.cancellationPolicy.reschedule}
              onChange={handleReschedulingChange}
              isDisabled={!formData.cancellationPolicy.enforce}
            />
            <Text ml={2} fontWeight="medium">
              Applied to Rescheduling
            </Text>
          </HStack>
        </Box>
      </Box>

      {/* Outgoing call announcement */}
      <Box mb={4} p={4} bgColor={bgColor} borderRadius="md">
        <Text fontSize="lg" mb={2} fontWeight="semibold">
          Outgoing call announcement
        </Text>
        <Text fontSize="sm" mb={4} color="gray.500">
          For outbound calls, we automatically play this announcement as soon as the recipient answers.
        </Text>

        <Box mb={4}>
          <HStack alignItems="center">
            <Switch
              isChecked={formData.outgoingAnnouncement.enabled}
              onChange={handleOutgoingEnabled}
            />
            <Text ml={2} fontWeight="medium">Enable</Text>
          </HStack>
        </Box>

        <Box>
          <Text fontWeight="medium" mb={1}>Announcement text</Text>
          <Textarea
            placeholder="e.g., “This is ACME Dental calling about your upcoming appointment…”"
            value={formData.outgoingAnnouncement.text}
            onChange={handleOutgoingText}
            isDisabled={!formData.outgoingAnnouncement.enabled}
            resize="vertical"
          />
        </Box>
      </Box>

      {/* Office Email Address */}
      <Box mb={4} p={4} bgColor={bgColor} borderRadius="md">
        <Text fontSize="lg" mb={2}>
          Office Email Address
        </Text>
        <HStack>
          <Text>{emailAddress}</Text>
          <CopyToClipboard
            text={emailAddress}
            onCopy={() => handleCopy("Email address")}
          >
            <Button leftIcon={<EmailIcon />} colorScheme="blue" size="sm">
              Copy to Clipboard
            </Button>
          </CopyToClipboard>
        </HStack>
      </Box>

      {/* Booking Link */}
      <Box mb={4} p={4} bgColor={bgColor} borderRadius="md">
        <Flex>
          <Text fontSize="lg" mb={2}>
            Online Booking Link
          </Text>
          <Link href={calendarLink} target="_blank">
            <ExternalLinkIcon ml="2" />
          </Link>
        </Flex>

        <HStack>
          <Text>{calendarLink}</Text>
          <CopyToClipboard
            text={calendarLink}
            onCopy={() => handleCopy("Calendar Link")}
          >
            <Button leftIcon={<CalendarIcon />} colorScheme="blue" size="sm">
              Copy to Clipboard
            </Button>
          </CopyToClipboard>
        </HStack>
      </Box>

      {/* Script snippet */}
      <Box mb={4} p={4} bgColor={bgColor} borderRadius="md">
        <Text fontSize="lg" mb={2}>
          Script Snippet
        </Text>
        <HStack>
          <Box
            p={2}
            bgColor="gray.800"
            color="white"
            borderRadius="md"
            fontFamily="monospace"
            flex="1"
          >
            {scriptSnippet}
          </Box>
          <CopyToClipboard
            text={scriptSnippet}
            onCopy={() => handleCopy("Script snippet")}
          >
            <Button leftIcon={<CopyIcon />} colorScheme="blue" size="sm">
              Copy to Clipboard
            </Button>
          </CopyToClipboard>
        </HStack>
      </Box>

      {/* Dev mode switch (if applicable) */}
      {process.env.NODE_ENV === "development" && process.env.REACT_APP_DEBUG && (
        <Box
          mb={4}
          p={4}
          outline="2px solid"
          outlineColor="orange.400"
          bgColor={bgColor}
          borderRadius="md"
        >
          <Flex>
            <Text fontSize="lg">Test/Debug mode</Text>
            <Spacer />
            <Switch
              id="testSwitch"
              size="lg"
              isChecked={isAppInTestDebugMode}
              onChange={(e) => setIsAppInTestDebugMode(e.target.checked)}
            />
          </Flex>
        </Box>
      )}
    </Box>
  );
};

export default DisplayInfo;
